import React, { useState } from "react";
import {
    Box,
    Button,
    ChakraProvider,
    FormControl,
    FormLabel,
    Input,
    useToast,
    VStack,
    Heading,
    Text,
    Divider,
} from "@chakra-ui/react";
import NavigationMenu from "../Navigation/NavigationMenu";
import { useNavigate } from "react-router-dom";

const DatabaseBackup = () => {
    const [selectedFile, setSelectedFile] = useState(null);
    const [selectedJsonFile, setSelectedJsonFile] = useState(null);
    const [isLowering, setIsLowering] = useState(false);
    const [isLoweringJson, setIsLoweringJson] = useState(false);
    const [isRestoring, setIsRestoring] = useState(false);
    const [isRestoringJson, setIsRestoringJson] = useState(false);
    const toast = useToast();
    const navigate = useNavigate();

    const handleFileChange = (event) => {
        setSelectedFile(event.target.files[0]);
    };

    const handleJsonFileChange = (event) => {
        setSelectedJsonFile(event.target.files[0]);
    };

    const handleJsonBackup = async () => {
        setIsLoweringJson(true);
        const accessToken = localStorage.getItem("access_token");

        try {
            const response = await fetch(
                `${process.env.REACT_APP_BACKEND_API || 'https://kidbackend.codingindia.co.in'}/HumanResource/api/database/backup/json/`,
                {
                    method: "GET",
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                    },
                }
            );

            if (!response.ok) {
                throw new Error("Failed to generate JSON backup");
            }

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", "kidney_backup.json");
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);

            toast({
                title: "JSON Backup Successful",
                description: "Your JSON data backup has been downloaded.",
                status: "success",
                duration: 3000,
                isClosable: true,
            });
        } catch (error) {
            toast({
                title: "JSON Backup Failed",
                description: error.message,
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setIsLoweringJson(false);
        }
    };

    const handleBackup = async () => {
        setIsLowering(true);
        const accessToken = localStorage.getItem("access_token");

        try {
            const response = await fetch(
                `${process.env.REACT_APP_BACKEND_API || `${process.env.REACT_APP_BACKEND_API || 'https://kidbackend.codingindia.co.in'}`}/HumanResource/api/database/backup/`,
                {
                    method: "GET",
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                    },
                }
            );

            if (!response.ok) {
                throw new Error("Failed to generate backup");
            }

            // Convert the response to a blob and download it
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", "kidney_backup.sql");
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);

            toast({
                title: "Backup Successful",
                description: "Your database backup has been downloaded.",
                status: "success",
                duration: 3000,
                isClosable: true,
            });
        } catch (error) {
            toast({
                title: "Backup Failed",
                description: error.message,
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setIsLowering(false);
        }
    };

    const handleRestore = async (e) => {
        e.preventDefault();
        if (!selectedFile) {
            toast({
                title: "No file selected",
                description: "Please select a backup SQL file to restore.",
                status: "warning",
                duration: 3000,
                isClosable: true,
            });
            return;
        }

        setIsRestoring(true);
        const accessToken = localStorage.getItem("access_token");
        const formData = new FormData();
        formData.append("file", selectedFile);

        try {
            const response = await fetch(
                `${process.env.REACT_APP_BACKEND_API || `${process.env.REACT_APP_BACKEND_API || 'https://kidbackend.codingindia.co.in'}`}/HumanResource/api/database/restore/`,
                {
                    method: "POST",
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                    },
                    body: formData,
                }
            );

            if (!response.ok) {
                const data = await response.json();
                throw new Error(data.error || "Failed to restore database");
            }

            toast({
                title: "Restore Successful",
                description: "Your database has been restored from the backup file.",
                status: "success",
                duration: 5000,
                isClosable: true,
            });
            setSelectedFile(null);
            // Optional: Redirect the user or sign them out after a successful restore due to state changes
        } catch (error) {
            toast({
                title: "Restore Failed",
                description: error.message,
                status: "error",
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setIsRestoring(false);
        }
    };

    const handleJsonRestore = async (e) => {
        e.preventDefault();
        if (!selectedJsonFile) {
            toast({
                title: "No file selected",
                description: "Please select a backup JSON file to restore.",
                status: "warning",
                duration: 3000,
                isClosable: true,
            });
            return;
        }

        setIsRestoringJson(true);
        const accessToken = localStorage.getItem("access_token");
        const formData = new FormData();
        formData.append("file", selectedJsonFile);

        try {
            const response = await fetch(
                `${process.env.REACT_APP_BACKEND_API || 'https://kidbackend.codingindia.co.in'}/HumanResource/api/database/restore/json/`,
                {
                    method: "POST",
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                    },
                    body: formData,
                }
            );

            if (!response.ok) {
                const data = await response.json();
                throw new Error(data.error || "Failed to restore JSON data");
            }

            toast({
                title: "JSON Restore Successful",
                description: "Your database has been restored from the JSON backup file.",
                status: "success",
                duration: 5000,
                isClosable: true,
            });
            setSelectedJsonFile(null);
        } catch (error) {
            toast({
                title: "JSON Restore Failed",
                description: error.message,
                status: "error",
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setIsRestoringJson(false);
        }
    };

    return (
        <>
            <NavigationMenu />
            <ChakraProvider>
                <Box p={8} maxW="800px" mx="auto" mt={10} bg="white" shadow="md" borderRadius="md">
                    <Heading as="h2" size="xl" mb={6} textAlign="center">
                        Database Backup & Restore
                    </Heading>

                    <VStack spacing={8} align="stretch" pb={10}>
                        {/* SQL Backup Section */}
                        <Box p={6} border="1px" borderColor="gray.200" borderRadius="md" bg="blue.50">
                            <Heading as="h3" size="md" mb={2} color="blue.700">
                                SQL Database Backup
                            </Heading>
                            <Text mb={4} color="gray.600">
                                Click the button below to generate and download a complete SQL dump of the current database.
                            </Text>
                            <Button
                                colorScheme="blue"
                                onClick={handleBackup}
                                isLoading={isLowering}
                                loadingText="Generating Backup..."
                                size="lg"
                                w="full"
                            >
                                Download SQL Backup (.sql)
                            </Button>
                        </Box>

                        {/* SQL Restore Section */}
                        <Box p={6} border="1px" borderColor="gray.200" borderRadius="md" bg="red.50">
                            <Heading as="h3" size="md" mb={2} color="red.700">
                                Restore SQL Database
                            </Heading>
                            <Text mb={4} color="gray.600">
                                <strong>Warning:</strong> Restoring a database will overwrite current data and terminate active connections. Please be sure before proceeding. Upload a `.sql` backup file.
                            </Text>
                            <form onSubmit={handleRestore}>
                                <FormControl mb={4}>
                                    <FormLabel>Select SQL Backup File</FormLabel>
                                    <Input
                                        type="file"
                                        accept=".sql"
                                        onChange={handleFileChange}
                                        bg="white"
                                        p={1}
                                    />
                                </FormControl>
                                <Button
                                    type="submit"
                                    colorScheme="red"
                                    isLoading={isRestoring}
                                    loadingText="Restoring Database..."
                                    size="lg"
                                    w="full"
                                >
                                    Restore SQL Database
                                </Button>
                            </form>
                        </Box>

                        <Divider />

                        {/* JSON Backup Section */}
                        <Box p={6} border="1px" borderColor="gray.200" borderRadius="md" bg="green.50">
                            <Heading as="h3" size="md" mb={2} color="green.700">
                                JSON Data Backup
                            </Heading>
                            <Text mb={4} color="gray.600">
                                Generate and download a JSON snapshot of the database data. This avoids raw SQL schema issues.
                            </Text>
                            <Button
                                colorScheme="green"
                                onClick={handleJsonBackup}
                                isLoading={isLoweringJson}
                                loadingText="Generating JSON Backup..."
                                size="lg"
                                w="full"
                            >
                                Download JSON Backup (.json)
                            </Button>
                        </Box>

                        {/* JSON Restore Section */}
                        <Box p={6} border="1px" borderColor="gray.200" borderRadius="md" bg="orange.50">
                            <Heading as="h3" size="md" mb={2} color="orange.700">
                                Restore JSON Data
                            </Heading>
                            <Text mb={4} color="gray.600">
                                <strong>Warning:</strong> Restoring JSON data will update existing database rows and add new ones based on the backup.
                            </Text>
                            <form onSubmit={handleJsonRestore}>
                                <FormControl mb={4}>
                                    <FormLabel>Select JSON Backup File</FormLabel>
                                    <Input
                                        type="file"
                                        accept=".json"
                                        onChange={handleJsonFileChange}
                                        bg="white"
                                        p={1}
                                    />
                                </FormControl>
                                <Button
                                    type="submit"
                                    colorScheme="orange"
                                    isLoading={isRestoringJson}
                                    loadingText="Restoring JSON Data..."
                                    size="lg"
                                    w="full"
                                >
                                    Restore JSON Data
                                </Button>
                            </form>
                        </Box>
                    </VStack>
                </Box>
            </ChakraProvider>
        </>
    );
};

export default DatabaseBackup;
